
















































































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { InjectReactive, Watch } from 'vue-property-decorator';

// Shared types
import { Company, RequestOfferRequest, Salutation, Link } from '@coldtec/cos-types';

// Store types
import { CompanyGetter } from '@/store/store.types';

// Validation
import { Errors } from '@/validation';

/**
 *  Holding the label and accepted proxy value
 *  for the terms and privacy checkbox.
 */
interface TermsPrivacy {
    label: string;
    accepted: boolean;
    error: string | boolean | null;
}

/**
 *  Component that renders the inputs for the personal data
 *  and binds the inputs to the request object.
 */
@Component
export default class PersonalDetailsData extends Vue {

    @InjectReactive('request')
    private request!: RequestOfferRequest;

    @InjectReactive('errors')
    private errors!: Errors<RequestOfferRequest>;

    /**
     *  Forwarding the salutations to the salutation select.
     */
    private salutations = Salutation;

    /**
     *  Proxy for the salutation, because keys are used
     *  for the select, but the API needs values.
     */
    private salutation: string | null = null;

    /**
     *  The terms and privacy label and accepted proxy.
     */
    private termsPrivacy: TermsPrivacy = {
        label: 'Ik ga akkoord met de {terms} en het verwerken van mijn persoonsgegevens {privacy}',
        accepted: false,
        error: null,
    };

    /**
     *  Interpolate the terms and privacy checkbox label with
     *  the company details and ensure the terms and privacy
     *  proxy state is equal to the request.
     */
    private created(): void {

        /**
         *  Interpolate the terms and privacy checkbox label with the company details.
         */
        this._interpolateTermsPrivacyLabel();

        /**
         *  Restore the client's salutation if already set.
         */
        if (this.request.client.salutation) {

            for (const [ key, value ] of Object.entries(this.salutations)) {

                if (this.request.client.salutation !== value) {

                    continue;

                }

                this.salutation = key;

                break;

            }

        }

        /**
         *  Ensure our proxy state is equal to the request data.
         */
        this.termsPrivacy.accepted = this.request.privacy_statement === 1 && this.request.terms_and_conditions === 1;

    }

    /**
     *
     *  Used to watch the salutation proxy and set the translated value in the request object.
     *
     *  (i) Because the API needs the values of the enum.
     *
     */
    @Watch('salutation')
    private _salutationUpdate(salutation: Salutation): void {

        for (const [ key, value ] of Object.entries(this.salutations)) {

            if (key !== salutation) {

                continue;

            }

            this.request.client.salutation = value;

            break;

        }

    }

    /**
     *
     *  Used to update the request's terms and privacy
     *  values accordingly once the user toggle its state.
     *
     *  (i) Translating the boolean because the API only accepts 0 or 1
     *
     */
    @Watch('termsPrivacy.accepted')
    private _termsPrivacyUpdate(accepted: boolean) {

        const value = accepted ? 1 : 0;

        this.request.terms_and_conditions = value;
        this.request.privacy_statement = value;

    }

    /**
     *  Watch and proxy the terms and privacy errors.
     */
    @Watch('errors.terms_and_conditions')
    @Watch('errors.privacy_statement')
    private _termsPrivacyErrorUpdate(error: string | boolean | null) {

        this.termsPrivacy.error = error;

    }

    /**
     *  Used to interpolate the terms and privacy label with the
     *  links of the company for which the tool is used.
     */
    private _interpolateTermsPrivacyLabel(): void {

        const company: Company = this.$store.getters[CompanyGetter.DETAILS];

        /**
         *  Create the default values.
         */
        let terms = 'Algemene Voorwaarden';
        let privacy = '.';

        if (Array.isArray(company.links)) {

            for (const link of company.links as Array<Link>) {

                if (link.key === 'terms.and.conditions') {

                    terms = `<a href="${ link.link }" title="Lees onze Algemene Voorwaarden" target="_blank" class="ccos-link ccos-link--external">Algemene Voorwaarden</a>'`;

                }

                if (link.key === 'privacy.statement') {

                    privacy = ` zoals staat omschreven in onze <a href="${ link.link }" title="Lees onze Privacyverklaring" target="_blank" class="ccos-link ccos-link--external">Privacyverklaring</a>.'`;

                }

            }

        }

        /**
         *  Replace the originals with the interpolated.
         */
        this.termsPrivacy.label = this.termsPrivacy.label.replace('{terms}', terms);
        this.termsPrivacy.label = this.termsPrivacy.label.replace('{privacy}', privacy);

    }

}
